import React, { useRef } from "react";
import { motion, useInView } from "motion/react";
import { Link } from "react-router-dom";
import { IconArrowRight, IconCheck } from "@tabler/icons-react";
import { staggerContainer, fadeInUp } from "./AnimationVariants";

const paths = [
  {
    who: "Gyms",
    title: "Lemonade Business",
    points: [
      "Class schedule and bookings",
      "Members, coaches and roles",
      "Memberships and payments",
    ],
    link: "/lemonade-business",
    cta: "Explore Lemonade Business",
  },
  {
    who: "Independent coaches",
    title: "Lemonade Coach",
    points: [
      "Build and share programming",
      "Track your clients' progress",
      "Your own landing page and payments",
    ],
    link: "/lemonade-coach",
    cta: "Explore Lemonade Coach",
  },
  {
    who: "Athletes",
    title: "The Lemonade app",
    points: ["Book classes in seconds", "See today's workout", "Log results and PRs"],
    link: "/for-athletes",
    cta: "See the app for athletes",
  },
];

export function AudiencePaths() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section ref={sectionRef} aria-labelledby="audience-title" className="w-full py-14 md:py-16">
      <motion.div
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={staggerContainer}
        className="mx-auto max-w-[1400px] w-[calc(100%-2rem)] md:w-[calc(100%-240px)] text-center lg:text-left"
      >
        <motion.p
          variants={fadeInUp}
          className="text-xs leading-none uppercase tracking-[0.12em] text-white/55"
        >
          Who it's for
        </motion.p>
        <motion.h2
          id="audience-title"
          variants={fadeInUp}
          className="mt-4 text-2xl md:text-3xl font-semibold tracking-tight text-white"
        >
          Built for everyone in the gym.
        </motion.h2>

        <ul className="mt-10 md:mt-12 mx-auto grid max-w-xl grid-cols-1 gap-4 text-left lg:max-w-none lg:grid-cols-3 lg:gap-6">
          {paths.map((p) => (
            <motion.li
              key={p.title}
              variants={fadeInUp}
              className="group relative flex flex-col rounded-2xl border border-white/10 bg-[#111110] p-6 md:p-7 transition-colors hover:border-white/30"
            >
              <p className="text-xs leading-none uppercase tracking-[0.12em] text-primary">
                {p.who}
              </p>
              <h3 className="mt-3 text-xl font-semibold text-white">{p.title}</h3>
              <ul className="mt-5 mb-6 flex flex-col gap-2.5 text-sm text-white/65">
                {p.points.map((point) => (
                  <li key={point} className="flex gap-2">
                    <IconCheck className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
                    {point}
                  </li>
                ))}
              </ul>
              {/* The link stretches over the whole card, so the card is one click target */}
              <Link
                to={p.link}
                className="mt-auto inline-flex min-h-11 items-center gap-1.5 self-start text-sm font-semibold text-white transition-colors group-hover:text-primary focus-visible:outline-none after:absolute after:inset-0 after:rounded-2xl focus-visible:after:ring-2 focus-visible:after:ring-primary"
              >
                {p.cta}
                <IconArrowRight
                  className="size-4 text-primary transition-transform group-hover:translate-x-0.5"
                  aria-hidden="true"
                />
              </Link>
            </motion.li>
          ))}
        </ul>
      </motion.div>
    </section>
  );
}
